import React, { useState } from "react";
import { Empty, Pill, Toggle } from "../ui";
import { Icon } from "../icons";
import { fmt, words } from "../format";

/**
 * The conversation as the user saw it, rebuilt from the agent's stored events
 * (`run_events`), in order: their messages, the agent's replies, the results
 * tables, the jobs it ran (research, saving, enriching) and how they ended,
 * its questions and errors. "Show activity" adds the working steps and the
 * tool calls behind them.
 *
 * A reply stored with `replay: true` is the only stored copy of text the
 * agent streamed (live tokens are not stored), so it is shown. A turn's
 * closing line (`final`) is shown only when it says something not already
 * said in that turn.
 */
export default function Transcript({ events }) {
  const [activity, setActivity] = useState(true);
  const items = buildItems(events ?? [], { activity });
  const total = (events ?? []).length;
  if (!total) return <Empty compact icon="conversations" title="Nothing in the transcript" />;
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "var(--aa-muted)" }}>
        <Toggle on={activity} onChange={setActivity}>
          Show activity
        </Toggle>
        <span>{fmt(total)} events recorded</span>
      </div>
      {items.map((it) => (
        <Item key={it.key} it={it} />
      ))}
    </div>
  );
}

const norm = (s) => String(s ?? "").replace(/\s+/g, " ").trim();

/** Events → what to draw. Pure. */
export function buildItems(events, { activity }) {
  const out = [];
  let said = []; // what this turn already showed, to drop a closing line that repeats it
  let stat = null;
  const push = (it) => out.push({ ...it, key: out.length });
  const repeats = (text) => {
    const t = norm(text);
    return !t || said.some((s) => s === t || s.includes(t) || t.includes(s));
  };

  for (const e of events) {
    const d = e?.data ?? {};
    switch (e?.type) {
      case "user":
        said = [];
        push({ kind: "user", text: d.text, files: d.files ?? [] });
        break;
      case "message":
        if (e.role && e.role !== "assistant") break;
        if (!norm(d.text)) break;
        said.push(norm(d.text));
        push({ kind: "agent", text: d.text });
        break;
      case "final":
        if (d.phase === "failed" || !repeats(d.text)) {
          if (norm(d.text)) said.push(norm(d.text));
          if (norm(d.text) || d.phase === "failed") push({ kind: "final", text: d.text, phase: d.phase });
        }
        break;
      case "ask_user":
        for (const q of d.questions ?? []) said.push(norm(q.question || q.header));
        push({ kind: "ask", questions: d.questions ?? [] });
        break;
      case "error":
        push({ kind: "error", text: d.message || d.code || "Error" });
        break;
      case "stat":
        stat = d;
        break;
      case "resultset":
        // The done envelope sends the turn's table again; draw it once.
        if (out.some((x) => x.kind === "table" && x.resultsetId && x.resultsetId === d.resultsetId)) break;
        push({ kind: "table", ...d, stat });
        stat = null;
        break;
      case "job_status":
        push({ kind: "job", ...d });
        break;
      case "low_credits":
        push({ kind: "credits", text: d.message, label: d.cta?.label });
        break;
      case "source_changed":
        push({ kind: "note", icon: "refresh", text: `Search source changed: ${d.from ?? "?"} → ${d.to ?? "?"}` });
        break;
      case "graph":
        push({ kind: "note", icon: "sparkle", text: `Proposed ${words(d.kind || "plan")}${d.meta?.listName ? `: ${d.meta.listName}` : ""}` });
        break;
      case "thought":
      case "live_status": {
        if (!activity || !norm(d.text)) break;
        const last = out[out.length - 1];
        if (last?.kind === "activity") {
          if (last.steps[last.steps.length - 1] !== norm(d.text)) last.steps.push(norm(d.text));
        } else push({ kind: "activity", steps: [norm(d.text)] });
        break;
      }
      case "tool_use":
        if (activity) push({ kind: "tool", id: d.id, name: d.name, args: d.args, result: undefined });
        break;
      case "tool_result": {
        if (!activity) break;
        const call = [...out].reverse().find((x) => x.kind === "tool" && x.id && x.id === d.id);
        if (call) {
          call.name = call.name || d.name;
          call.ok = d.ok;
          call.result = d.result;
        } else push({ kind: "tool", id: d.id, name: d.name, ok: d.ok, result: d.result });
        break;
      }
      default:
        // session_title, ui_action, token: nothing the user read.
        break;
    }
  }
  return out;
}

function Item({ it }) {
  switch (it.kind) {
    case "user":
      return (
        <div className="aa-bubble-user">
          {it.text || <em style={{ opacity: 0.8 }}>(picked an option)</em>}
          {it.files.length > 0 && (
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name="file" size={13} />
              {it.files.map((x) => x.filename).join(", ")}
            </div>
          )}
        </div>
      );
    case "agent":
      return <div className="aa-bubble-agent">{it.text}</div>;
    case "final":
      return (
        <div className="aa-bubble-agent" style={it.phase === "failed" ? { borderColor: "var(--aa-bad-ink)" } : undefined}>
          {it.phase === "failed" && <Label tone="bad">Turn ended as failed</Label>}
          {it.text || <em style={{ color: "var(--aa-muted)" }}>(no closing text)</em>}
        </div>
      );
    case "ask":
      return (
        <div style={{ maxWidth: "86%", border: "1px dashed var(--aa-border-strong)", borderRadius: 12, padding: "10px 14px", fontSize: 13, background: "var(--aa-surface-2)" }}>
          <Label>Asked the user</Label>
          {it.questions.map((q, j) => (
            <div key={j} style={{ marginBottom: 6 }}>
              <div style={{ fontWeight: 500 }}>{q.question || q.header || "Question"}</div>
              {q.options?.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                  {q.options.map((o, k) => (
                    <Pill key={k} tone="neutral">
                      {o.label}
                    </Pill>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    case "error":
      return (
        <Callout tone="bad" icon="alert">
          {it.text}
        </Callout>
      );
    case "credits":
      return (
        <Callout tone="warn" icon="credit">
          {it.text}
          {it.label && <span style={{ opacity: 0.8 }}> · offered “{it.label}”</span>}
        </Callout>
      );
    case "table":
      return <ResultTable it={it} />;
    case "job":
      return <JobLine it={it} />;
    case "note":
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--aa-muted)" }}>
          <Icon name={it.icon} size={13} />
          {it.text}
        </div>
      );
    case "activity":
      return (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 12, color: "var(--aa-faint)" }}>
          <Icon name="activity" size={13} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{it.steps.join(" → ")}</span>
        </div>
      );
    case "tool":
      return <ToolCall it={it} />;
    default:
      return null;
  }
}

const TONES = {
  bad: { color: "var(--aa-bad-ink)", background: "var(--aa-bad-soft)" },
  warn: { color: "var(--aa-warn-ink)", background: "var(--aa-warn-soft)" },
  good: { color: "var(--aa-good-ink)", background: "var(--aa-good-soft)" },
};

function Label({ children, tone }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: tone ? TONES[tone].color : "var(--aa-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>
      {children}
    </div>
  );
}

function Callout({ tone, icon, children }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", ...TONES[tone], borderRadius: 10, padding: "8px 12px", fontSize: 13, maxWidth: "86%" }}>
      <Icon name={icon} size={15} style={{ marginTop: 1, flexShrink: 0 }} />
      <span>{children}</span>
    </div>
  );
}

const JOB_KIND = { research: "Research", import: "Saving", enrichment: "Enriching" };
const JOB_STATUS = {
  waiting: { label: "running", tone: "neutral", icon: "clock" },
  running: { label: "running", tone: "neutral", icon: "clock" },
  completed: { label: "completed", tone: "good", icon: "checkCircle" },
  failed: { label: "failed", tone: "bad", icon: "xCircle" },
  cancelled: { label: "cancelled", tone: "warn", icon: "xCircle" },
};

function JobLine({ it }) {
  const st = JOB_STATUS[it.status] ?? { label: words(it.status || "update"), tone: "neutral", icon: "activity" };
  const detail = it.message || it.title || it.segmentName;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, maxWidth: "86%", border: "1px solid var(--aa-border)", borderRadius: 10, padding: "8px 12px", background: "var(--aa-surface)" }}>
      <Icon name={st.icon} size={15} style={{ marginTop: 2, flexShrink: 0, color: TONES[st.tone]?.color ?? "var(--aa-muted)" }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <b>{JOB_KIND[it.kind] ?? words(it.kind || "Job")} job</b>
          <Pill tone={st.tone} dot>
            {st.label}
          </Pill>
          {it.segmentName && <span style={{ color: "var(--aa-muted)" }}>into {it.segmentName}</span>}
        </div>
        {detail && detail !== it.segmentName && <div style={{ color: "var(--aa-ink-2)", marginTop: 4 }}>{detail}</div>}
        {it.jobId && <div className="aa-mono" style={{ color: "var(--aa-faint)", marginTop: 4 }}>{it.jobId}</div>}
      </div>
    </div>
  );
}

const MAX_COLUMNS = 12;

function ResultTable({ it }) {
  const rows = Array.isArray(it.preview) ? it.preview : [];
  const declared = Array.isArray(it.columns) ? it.columns.filter((c) => c?.key) : [];
  const columns = (declared.length ? declared : Object.keys(rows[0] ?? {}).map((k) => ({ key: k, label: k }))).slice(0, MAX_COLUMNS);
  const total = typeof it.total === "number" ? it.total : null;
  const hidden = (declared.length || Object.keys(rows[0] ?? {}).length) - columns.length;
  return (
    <div style={{ border: "1px solid var(--aa-border)", borderRadius: 12, background: "var(--aa-surface)", overflow: "hidden", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", fontSize: 13, flexWrap: "wrap" }}>
        <Icon name="database" size={15} />
        <b>Results shown</b>
        <span style={{ color: "var(--aa-muted)" }}>
          {fmt(rows.length)} row{rows.length === 1 ? "" : "s"}
          {total != null && total !== rows.length ? ` of ${fmt(total)}` : ""}
          {it.entityType ? ` · ${words(it.entityType)}` : ""}
          {hidden > 0 ? ` · ${hidden} more column${hidden === 1 ? "" : "s"} not shown` : ""}
        </span>
        {it.resultsetId && (
          <span className="aa-mono" style={{ marginLeft: "auto", color: "var(--aa-faint)" }}>
            {it.resultsetId}
          </span>
        )}
      </div>
      {rows.length > 0 ? (
        <div style={{ overflow: "auto", maxHeight: 320 }}>
          <table className="aa-table" style={{ width: "max-content", minWidth: "100%" }}>
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key}>{c.label || c.key}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c.key} style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <Cell value={r?.[c.key]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: "0 14px 12px", fontSize: 12, color: "var(--aa-muted)" }}>No rows came back.</div>
      )}
    </div>
  );
}

function Cell({ value }) {
  if (value == null || value === "") return <span style={{ color: "var(--aa-faint)" }}>—</span>;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    const s = JSON.stringify(value);
    return <span title={s}>{s.length > 80 ? `${s.slice(0, 80)}…` : s}</span>;
  }
  const s = String(value);
  if (/^https?:\/\//i.test(s)) {
    return (
      <a href={s} target="_blank" rel="noreferrer" title={s}>
        {s.replace(/^https?:\/\/(www\.)?/i, "").slice(0, 40)}
      </a>
    );
  }
  return <span title={s}>{s}</span>;
}

const MAX_JSON = 4000;

function json(v) {
  if (v === undefined) return null;
  let s;
  try {
    s = JSON.stringify(v, null, 2);
  } catch {
    s = String(v);
  }
  return s.length > MAX_JSON ? `${s.slice(0, MAX_JSON)}\n…` : s;
}

function ToolCall({ it }) {
  const failed = it.ok === false;
  const args = json(it.args);
  const result = json(it.result);
  return (
    <details style={{ fontSize: 12, maxWidth: "86%" }}>
      <summary style={{ cursor: "pointer", color: failed ? "var(--aa-bad-ink)" : "var(--aa-muted)", display: "flex", alignItems: "center", gap: 6 }}>
        <Icon name="capabilities" size={13} />
        <span>
          Tool call <span className="aa-mono">{it.name || "tool"}</span>
          {failed ? " · failed" : it.result === undefined ? " · no result recorded" : ""}
        </span>
      </summary>
      {args && <Pre label="Input">{args}</Pre>}
      {result && <Pre label="Result">{result}</Pre>}
    </details>
  );
}

function Pre({ label, children }) {
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ color: "var(--aa-muted)", marginBottom: 2 }}>{label}</div>
      <pre className="aa-mono" style={{ margin: 0, padding: "8px 10px", background: "var(--aa-surface-2)", border: "1px solid var(--aa-border)", borderRadius: 8, maxHeight: 240, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {children}
      </pre>
    </div>
  );
}
